import {inject, Injectable} from '@angular/core';
import {firstValueFrom, merge, NEVER, Observable, Subject} from 'rxjs';
import {switchMap, takeUntil, tap} from 'rxjs/operators';
import {destroy$} from '../../../components/destroyable';
import {DexStreamingService} from '../../../components/dex/dex-streaming/dex-streaming.service';
import {DexOrderTxSubjectService} from '../../../components/dex/dex-streaming/subjects/dex-order-tx-subject-service';
import {
    DexSecuritiesSubjectService
} from '../../../components/dex/dex-streaming/subjects/dex-securities-subject-service';
import {DeliverableId} from '../../../components/dex/dex-streaming/types/deliverable';
import {DexOrderFromApiStatus} from '../../../components/dex/dex-streaming/types/dex-order';
import {Erc20BalanceUpdatesService} from '../../../components/dex/dyn-balances/erc20-balances-updates.service';
import {DisplayTxHistoryFactory} from '../display-tx-history.factory';
import {TxHistoryRec, TxHistoryRecMethod, TxHistoryStatus, TxHistoryTransferMeta} from '../tx-history-rec';
import {DexFilterOTxByUserWalletsService} from './dex-filter-otx-by-user-wallets.service';
import {DexOrderTx} from './dex-order-tx';
import {TransactionApiService} from './transaction-api.service';
import {TxApiStorageMiddlewareService} from './tx-api-storage-middleware.service';

@Injectable()
export class TxMonitoringStorageService {

    public readonly anyTxUpdate$: Observable<TxHistoryRec>;

    private readonly destroy$ = destroy$();

    // TODO: Must be reset per User!
    private readonly storage = new Map<string, TxHistoryRec>();

    private storagePending = new Map<string, TxHistoryRec>();
    private storageConfirmed = new Map<string, TxHistoryRec>();
    private storageFailedToRetry = new Map<string, TxHistoryRec>();

    private readonly anyTxUpdateSubject$ = new Subject<TxHistoryRec>();

    private initPromise: Promise<void>;

    private readonly transactionApiService = inject(TransactionApiService);
    private readonly txApiStorageMiddlewareService = inject(TxApiStorageMiddlewareService);
    private readonly dexOrderTxSubjectService = inject(DexOrderTxSubjectService);
    private readonly dexStreamingService = inject(DexStreamingService);
    private readonly dexFilterOTxByUserWalletsService = inject(DexFilterOTxByUserWalletsService);
    private readonly displayTxHistoryFactory = inject(DisplayTxHistoryFactory);
    private readonly dexSecuritiesSubjectService = inject(DexSecuritiesSubjectService);
    private readonly erc20BalanceUpdatesService = inject(Erc20BalanceUpdatesService);

    constructor() {
        this.anyTxUpdate$ = this.anyTxUpdateSubject$.asObservable();

        // TODO
        (window as any).txMonitoringStorageService = this;
    }

    public async init(): Promise<void> {
        const doInit = async () => {
            await this.txApiStorageMiddlewareService.init();

            const txs$ = this.transactionApiService.getAllTransactionsByStatuses([
                TxHistoryStatus.PENDING,
                TxHistoryStatus.CONFIRMED
            ]);

            const stream$ = txs$.pipe(
                tap(txs => {
                    txs.forEach(tx => {
                        if (this.isTxStatusTemporary(tx.status)) {
                            this.setInStorage(tx);
                        }
                    });
                }),
                takeUntil(this.destroy$)
            );

            this.clearStorages();
            await firstValueFrom(stream$);

            this.listenToApiStorage();
        };

        this.initPromise ??= doInit();
        await this.initPromise;
    }

    public hasTransactionsToMonitor(includeFailedToRetry: boolean = false): boolean {
        return includeFailedToRetry
            ? this.storage.size > 0
            : this.storagePending.size > 0 || this.storageConfirmed.size > 0;
    }

    public getTransactionByHash(txHash: string): TxHistoryRec | undefined {
        return this.storage.get(txHash);
    }

    public getPendingTransactions(includeFailedToRetry: boolean = false): ReadonlyArray<TxHistoryRec> {
        const list = [...this.storagePending.values()];

        if (includeFailedToRetry && !!this.storageFailedToRetry.size) {
            list.push(...this.storageFailedToRetry.values());
        }

        return list;
    }

    public getConfirmedTransactions(): ReadonlyArray<TxHistoryRec> {
        return [...this.storageConfirmed.values()];
    }

    public async updateTx(txHash: string, changes: Partial<TxHistoryRec>): Promise<TxHistoryRec | undefined> {
        const knownTx = this.storage.get(txHash);
        let tx: TxHistoryRec | undefined;

        if (knownTx) {
            tx = {
                ...knownTx,
                ...changes
            };

            this.updateInStorage(tx);
            await this.txApiStorageMiddlewareService.asyncUpdateTransaction(tx);
        }

        return tx;
    }

    private clearStorages(): void {
        this.storage.clear();
        this.storagePending.clear();
        this.storageConfirmed.clear();
        this.storageFailedToRetry.clear();
    }

    private setInStorage(tx: TxHistoryRec): void {
        this.storage.set(tx.hash, tx);

        switch (tx.status) {
            case TxHistoryStatus.PENDING: {
                this.storagePending.set(tx.hash, tx);
                break;
            }
            case TxHistoryStatus.CONFIRMED: {
                this.storageConfirmed.set(tx.hash, tx);
                break;
            }
        }

        this.anyTxUpdateSubject$.next(tx);
    }

    private deleteFromStorage(tx: TxHistoryRec): void {
        if (this.storage.has(tx.hash)) {
            this.storage.delete(tx.hash);
            this.anyTxUpdateSubject$.next(tx);
        }
    }

    private updateInStorage(tx: TxHistoryRec): void {
        const {hash, status} = tx;
        const known = this.storage.get(hash);

        if (known) {
            if (known.status !== status) {
                this.storagePending.delete(hash);
                this.storageConfirmed.delete(hash);
            }

            if (this.isTxStatusTemporary(status)) {
                this.setInStorage(tx);
            } else {
                this.deleteFromStorage(tx);
            }
        }
    }

    private updateOrCreateInStorage(tx: TxHistoryRec): void {
        if (this.storage.has(tx.hash)) {
            this.updateInStorage(tx);
        } else if (this.isTxStatusTemporary(tx.status)) {
            this.setInStorage(tx);
        }
    }

    private listenToApiStorage(): void {
        const txAdded$ = this.transactionApiService.txAdded$.pipe(
            tap(tx =>
                this.updateOrCreateInStorage(tx)
            )
        );

        const oTxUpdated$ = this.dexFilterOTxByUserWalletsService.getUserWalletAddressesStream().pipe(
            switchMap(addresses => addresses?.length
                ? this.dexStreamingService.createSubscription(this.dexOrderTxSubjectService.type, addresses)
                : NEVER
            ),
            tap(oTxs => {
                for (const oTx of oTxs) {
                    const tx = oTx.transaction;

                    if (tx) {
                        this.updateOrCreateInStorage(tx);
                    }
                }

                this.updateBalances(oTxs);
            })
        );

        // Updates are not handled here, because this service should already have all of them.
        merge(txAdded$, oTxUpdated$).pipe(
            takeUntil(this.destroy$)
        ).subscribe();
    }

    private isTxStatusTemporary(status: TxHistoryStatus): boolean {
        return status === TxHistoryStatus.PENDING || status === TxHistoryStatus.CONFIRMED;
    }

    private updateBalances(oTxs: DexOrderTx[]): void {
        const rawDIds: DeliverableId[] = [];
        const rawAddresses: string[] = [];

        for (const oTx of oTxs) {
            const th = oTx.transaction;
            const o = oTx.order;
            const trades = oTx.trades;

            if (o && o.status === DexOrderFromApiStatus.executed && trades?.length) {
                if (o.address) {
                    const displayTh = this.displayTxHistoryFactory.create(oTx);
                    const sent = displayTh.$amount()?.d.deliverableId;
                    const received = displayTh.$received()?.map(it => it.d.deliverableId)
                        || [];

                    rawAddresses.push(o.address);
                    rawDIds.push(this.dexSecuritiesSubjectService.$nativeDel()!.deliverableId, sent, ...received);
                }
            } else if (th && th.metadata.method === TxHistoryRecMethod.TRANSFER) {
                if (th.from) {
                    rawAddresses.push(th.from, th.to);
                    rawDIds.push(this.dexSecuritiesSubjectService.$nativeDel()!.deliverableId);

                    const transferMeta = th.metadata as TxHistoryTransferMeta;

                    if (transferMeta) {
                        rawDIds.push(Number(transferMeta.deliverableId));
                    }
                }
            }
        }

        if (rawDIds.length && rawAddresses.length) {
            const dIds = [...new Set(rawDIds.filter(Boolean))];
            const wAddresses = [...new Set(rawAddresses.filter(Boolean))];

            if (dIds.length && wAddresses.length) {
                this.erc20BalanceUpdatesService.notifyUpdates({wAddresses, dIds});
            }
        }
    }

}
